import { WorkshopV2 } from '../../../interfaces/workshop.interface';
import { Participant } from '../../../interfaces/reservation.interface';
import { reservationPlaces } from '../../../utils/reservation';
import {
  formatClosingDate,
  isBookingClosed,
} from '../../../utils/workshopClosing';

/**
 * Dérivations du tableau de bord : fonctions pures, sans React ni Firestore.
 *
 * Deux règles gouvernent tout ce fichier :
 * 1. Le remplissage se calcule TOUJOURS depuis les réservations réellement lues
 *    (`Σ reservationPlaces`), jamais depuis `remainingPlaces` : ce compteur
 *    dérive (EditWorkshopPopup change `maxParticipants` sans le recalculer, et
 *    AddReservationPopUp enchaîne addDoc puis updateDoc hors transaction).
 * 2. « Aujourd'hui » se juge sur la date civile locale du navigateur ; seules
 *    les échéances de fermeture passent par `formatClosingDate`, qui épingle
 *    Europe/Paris. Les deux mécanismes ne se mélangent pas.
 */

export const numberFormat = new Intl.NumberFormat('fr-FR');

const DAY_MS = 24 * 60 * 60 * 1000;

/** Fenêtre de la tuile « Places à vendre ». */
export const SELL_HORIZON_DAYS = 7;

/**
 * Plafond de l'appel `listGiftcards`. Exporté parce que le panneau l'annonce
 * mot pour mot : un décompte plafonné qui ne dit pas son plafond est un chiffre
 * faux.
 */
export const GIFTCARD_FETCH_LIMIT = 100;

/** Un atelier vide à moins de ce délai devient une alerte. */
const NO_BOOKING_HORIZON_DAYS = 7;

/** Une fermeture sous ce délai devient une alerte si le remplissage est faible. */
const CLOSING_SOON_MS = DAY_MS;

/** En dessous de ce taux, une fermeture imminente vaut la peine d'être signalée. */
const LOW_FILL_RATE = 60;

// ============================================
// Dates
// ============================================

export const startOfDay = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

export const startOfToday = (now: Date = new Date()): Date => startOfDay(now);

export const isSameCivilDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Écart en jours civils : « dans 1 jour » vaut demain, pas dans 24 heures. */
export const civilDaysBetween = (from: Date, to: Date): number =>
  Math.round(
    (startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS
  );

/** « samedi 26 juillet à 14:00 » — à accompagner de `first-letter:uppercase`. */
export const longDate = (date: Date): string =>
  date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

/** « 26 juillet » — pour annoncer les bornes de la fenêtre observée. */
export const dayAndMonth = (date: Date): string =>
  date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

// ============================================
// Ateliers
// ============================================

/**
 * Ramène un document Firestore à quelque chose que le reste de la page peut
 * lire sans se protéger à chaque ligne. Une page qui agrège beaucoup de
 * documents est l'endroit le plus probable pour en croiser un de malformé.
 *
 * - `date` qui n'est pas une Timestamp : document écarté, il n'y a rien à en
 *   tirer et `.toDate()` blanchirait tout l'écran.
 * - `bookingClosesAt` qui n'est pas une Timestamp : ramené à `null`.
 *   `isBookingClosed` appelle `.toMillis()` sans garde, et le champ est
 *   simplement absent sur les ateliers créés depuis un modèle comme sur tous
 *   ceux antérieurs à la fonctionnalité. Absent signifie « ouvert ».
 */
export const normalizeWorkshop = (raw: WorkshopV2): WorkshopV2 | null => {
  if (typeof raw.date?.toDate !== 'function') return null;

  const closesAt = raw.bookingClosesAt;
  if (closesAt && typeof closesAt.toMillis === 'function') return raw;
  return { ...raw, bookingClosesAt: null };
};

/** Instant de fermeture des réservations, ou `null` si l'atelier reste ouvert. */
export const closingDate = (workshop: WorkshopV2): Date | null => {
  const closesAt = workshop.bookingClosesAt;
  if (!closesAt || typeof closesAt.toMillis !== 'function') return null;
  return closesAt.toDate();
};

/**
 * « Ouvert » = on peut encore y placer quelqu'un.
 *
 * `active` (publié ou non) et `bookingClosesAt` (instant de fermeture) sont
 * orthogonaux : il faut les trois conditions. On passe systématiquement par
 * `isBookingClosed`, qui encode « champ absent ⇒ ouvert » et la logique de
 * fuseau — jamais de comparaison de timestamps à la main.
 */
export const isOpen = (workshop: WorkshopV2, now: Date): boolean =>
  workshop.active === true &&
  !isBookingClosed(workshop, now) &&
  workshop.date.toDate().getTime() > now.getTime();

/** Ateliers du jour, du plus tôt au plus tard. */
export const workshopsToday = (
  workshops: WorkshopV2[],
  now: Date
): WorkshopV2[] =>
  workshops
    .filter((workshop) => isSameCivilDay(workshop.date.toDate(), now))
    .sort((a, b) => a.date.toMillis() - b.date.toMillis());

/** Premier atelier à venir. La requête arrivant déjà triée, `find` suffit. */
export const nextWorkshop = (
  workshops: WorkshopV2[],
  now: Date
): WorkshopV2 | null =>
  workshops.find(
    (workshop) => workshop.date.toDate().getTime() > now.getTime()
  ) || null;

/** Ateliers à venir, ouverts aux réservations. */
export const openUpcoming = (
  workshops: WorkshopV2[],
  now: Date
): WorkshopV2[] => workshops.filter((workshop) => isOpen(workshop, now));

/**
 * Ateliers à venir sur lesquels on ne peut plus rien faire : inactifs, ou
 * réservations déjà closes. Ils sont exclus de « À remplir », mais comptés —
 * sans ça, une liste vide se lirait comme « tout va bien ».
 */
export const countUnactionableUpcoming = (
  workshops: WorkshopV2[],
  now: Date
): number =>
  workshops.filter(
    (workshop) =>
      workshop.date.toDate().getTime() > now.getTime() &&
      !isOpen(workshop, now)
  ).length;

// ============================================
// Réservations
// ============================================

/** Places occupées : une place = un plan de travail, un duo en occupe une. */
export const placesOf = (rows: Participant[]): number =>
  rows.reduce((sum, row) => sum + reservationPlaces(row), 0);

/**
 * Nombre de personnes qui franchissent la porte — le chiffre de mise en place.
 *
 * Un duo occupe une place mais amène deux personnes. Une réservation sans
 * formule (`type === null` : toutes les résas antérieures au modèle V2) compte
 * pour une personne, et l'interface le DIT plutôt que de l'inventer.
 */
export const headcountOf = (rows: Participant[]): number =>
  rows.reduce(
    (sum, row) => sum + reservationPlaces(row) * (row.type === 'duo' ? 2 : 1),
    0
  );

export const duoCount = (rows: Participant[]): number =>
  rows.filter((row) => row.type === 'duo').length;

export const unknownTypeCount = (rows: Participant[]): number =>
  rows.filter((row) => row.type === null).length;

export const fillRate = (places: number, capacity: number): number =>
  capacity > 0
    ? Math.min(100, Math.max(0, Math.round((places / capacity) * 100)))
    : 0;

/** Réservations d'une liste d'ateliers, aplaties. */
export const rowsFor = (
  workshops: WorkshopV2[],
  participants: Record<string, Participant[]>
): Participant[] =>
  workshops.flatMap((workshop) => participants[workshop.id] || []);

export const capacityOf = (workshops: WorkshopV2[]): number =>
  workshops.reduce((sum, workshop) => sum + (workshop.maxParticipants || 0), 0);

// ============================================
// Bande d'alerte
// ============================================

export interface DashboardAlert {
  id: string;
  level: 'critical' | 'warning';
  icon: 'timer' | 'calendarX';
  text: string;
}

/** Ordre des règles. L'urgence prime sur la chronologie. */
const RANK_CLOSING_SOON = 0;
const RANK_NO_BOOKING = 1;

interface SortableAlert extends DashboardAlert {
  /** Règle qui a produit la ligne : c'est la clé de tri principale. */
  rank: number;
  /** Date de l'atelier concerné : départage les alertes de même règle. */
  dateMs: number;
}

/**
 * Les points qui demandent une action, du plus urgent au moins urgent.
 *
 * Une seule ligne par atelier, la plus grave : deux lignes sur le même atelier
 * satureraient la bande et masqueraient les autres. Les règles sont donc
 * évaluées dans l'ordre, la première qui correspond gagne.
 *
 * Le tri se fait sur la règle, PAS sur la date : la bande est tronquée, et une
 * échéance imminente doit survivre à la troncature même si elle concerne un
 * atelier plus lointain qu'un autre.
 */
export const buildAlerts = (
  workshops: WorkshopV2[],
  participants: Record<string, Participant[]>,
  now: Date
): DashboardAlert[] => {
  const alerts: SortableAlert[] = [];

  workshops.forEach((workshop) => {
    const rows = participants[workshop.id] || [];
    const places = placesOf(rows);
    const capacity = workshop.maxParticipants || 0;
    const date = workshop.date.toDate();
    const dateMs = date.getTime();
    const title = workshop.title || 'Atelier sans titre';
    const open = isOpen(workshop, now);
    const closesAt = closingDate(workshop);

    // 1. Fermeture imminente sur un atelier peu rempli : la dernière fenêtre
    //    pour pousser une communication.
    if (
      open &&
      closesAt &&
      closesAt.getTime() - now.getTime() <= CLOSING_SOON_MS &&
      fillRate(places, capacity) < LOW_FILL_RATE
    ) {
      const free = Math.max(0, capacity - places);
      alerts.push({
        id: workshop.id,
        level: 'critical',
        icon: 'timer',
        rank: RANK_CLOSING_SOON,
        dateMs,
        text: `Les réservations de « ${title} » ferment ${formatClosingDate(
          closesAt
        )} — il reste ${free} place${free > 1 ? 's' : ''} libre${
          free > 1 ? 's' : ''
        }.`,
      });
      return;
    }

    // 2. Atelier proche et totalement vide.
    if (open && places === 0) {
      const days = civilDaysBetween(now, date);
      if (days <= NO_BOOKING_HORIZON_DAYS) {
        alerts.push({
          id: workshop.id,
          level: 'warning',
          icon: 'calendarX',
          rank: RANK_NO_BOOKING,
          dateMs,
          text:
            days <= 0
              ? `${title} a lieu aujourd'hui et n'a aucune réservation.`
              : `${title} a lieu dans ${days} jour${
                  days > 1 ? 's' : ''
                } et n'a aucune réservation.`,
        });
      }
    }
  });

  return alerts
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.dateMs - b.dateMs;
    })
    .map(({ dateMs, rank, ...alert }) => alert);
};

// ============================================
// Panneau « À remplir »
// ============================================

export interface UpcomingRow {
  workshop: WorkshopV2;
  places: number;
  capacity: number;
  fillRate: number;
  closesAt: Date | null;
}

/**
 * Ateliers à venir encore ouverts, du moins rempli au plus rempli.
 *
 * Le tri bascule : tant que les réservations ne sont pas chargées, on trie par
 * date. Trier sur un remplissage inconnu ferait sauter toutes les lignes au
 * moment précis où les réservations arrivent.
 */
export const buildUpcomingRows = (
  workshops: WorkshopV2[],
  participants: Record<string, Participant[]>,
  now: Date,
  participantsReady: boolean
): UpcomingRow[] => {
  const rows = openUpcoming(workshops, now).map((workshop) => {
    const places = placesOf(participants[workshop.id] || []);
    const capacity = workshop.maxParticipants || 0;
    return {
      workshop,
      places,
      capacity,
      fillRate: fillRate(places, capacity),
      closesAt: closingDate(workshop),
    };
  });

  return rows.sort((a, b) => {
    const byDate = a.workshop.date.toMillis() - b.workshop.date.toMillis();
    if (!participantsReady) return byDate;
    return a.fillRate - b.fillRate || byDate;
  });
};
